import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ActivityPlans from "@/components/ActivityPlans";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { RoleBadge, fmtDateTime } from "@/components/badges";
import { Info, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Progress() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { selectedId } = useProject();
  const { user } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", party_id: "", weight: 1, planned_date: "", start_date: "" });
  const [editForm, setEditForm] = useState({ progress: 0, note: "", weight: 1, planned_date: "", start_date: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [highlightId, setHighlightId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["work-items", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/work-items`)).data,
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!focusId || !data) return undefined;
    const row = document.querySelector(`[data-testid="work-item-row-${focusId}"]`);
    if (!row) return undefined;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(focusId);
    setSearchParams({}, { replace: true });
    const tm = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(tm);
  }, [focusId, data, setSearchParams]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["work-items", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };

  const addMut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${selectedId}/work-items`, { ...form, party_id: form.party_id || undefined, weight: Number(form.weight) || 1, planned_date: form.planned_date || null, start_date: form.start_date || null })).data,
    onSuccess: () => {
      toast.success(t("progress.addedToast"));
      setAddOpen(false);
      setForm({ title: "", description: "", party_id: "", weight: 1, planned_date: "", start_date: "" });
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const editMut = useMutation({
    mutationFn: async () => (await api.put(`/work-items/${editItem.id}`, { progress: editForm.progress, note: editForm.note, weight: Number(editForm.weight) || undefined, planned_date: editForm.planned_date || undefined, start_date: editForm.start_date })).data,
    onSuccess: () => {
      toast.success(t("progress.updatedToast"));
      setEditItem(null);
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/work-items/${id}`),
    onSuccess: () => {
      toast.success(t("progress.deletedToast"));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const groups = useMemo(() => {
    const g = {};
    (data?.items || []).forEach((it) => {
      const key = it.company_name;
      g[key] = g[key] || { company: it.company_name, role: it.role, items: [] };
      g[key].items.push(it);
    });
    return Object.values(g).sort((a, b) => a.company.localeCompare(b.company));
  }, [data]);

  const mergedView = data ? !data.can_view_all && !data.is_admin : false;

  const workItemsSection = (
    <>
      {data?.is_admin && (
        <div className="flex justify-end">
          <Button onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-work-item-button">
            <Plus className="mr-2 h-4 w-4" /> {t("progress.addWorkItem")}
          </Button>
        </div>
      )}

      {mergedView && (
        <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900" data-testid="progress-visibility-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {t("progress.visibilityNote")}
        </div>
      )}

      {isLoading && <p className="text-sm text-slate-500" data-testid="progress-loading">{t("common.loading")}</p>}
      {data && groups.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="progress-empty">
          {mergedView ? t("progress.emptyMerged") : t("progress.emptyAll")}
        </p>
      )}

      {groups.map((g) => {
        const avg = Math.round((g.items.reduce((s, i) => s + i.progress, 0) / g.items.length) * 10) / 10;
        return (
          <Card key={g.company} data-testid={`party-section-${g.company.replace(/[^a-z0-9]+/gi, "-")}`}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  {g.company} <RoleBadge role={g.role} />
                </CardTitle>
                <div className="flex w-40 items-center gap-2">
                  <ProgressBar value={avg} className="h-1.5 flex-1" />
                  <span className="font-mono text-sm font-semibold">{avg}%</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("progress.thWorkItem")}</TableHead>
                    <TableHead className="w-48">{t("progress.thProgress")}</TableHead>
                    <TableHead>{t("progress.thLastUpdate")}</TableHead>
                    <TableHead className="w-24 text-right">{t("progress.thActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.items.map((it) => (
                    <TableRow key={it.id} className={highlightId === it.id ? "bg-sky-100/70 transition-colors" : ""} data-testid={`work-item-row-${it.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{it.title}</div>
                        {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                        <div className="mt-0.5 font-mono text-[10px] text-slate-400">{t("progress.weight")} {it.weight ?? 1}{it.planned_date ? ` · ${t("progress.planShort")} ${it.planned_date}` : ""}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ProgressBar value={it.progress} className="h-1.5 flex-1" />
                          <span className="w-12 text-right font-mono text-sm font-semibold" data-testid={`work-item-progress-${it.id}`}>{it.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {it.updated_by} · {fmtDateTime(it.updated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {it.can_edit && (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t("progress.updateProgress")}
                              onClick={() => {
                                setEditItem(it);
                                setEditForm({ progress: it.progress, note: "", weight: it.weight || 1, planned_date: it.planned_date || "", start_date: it.start_date || "" });
                              }}
                              data-testid={`work-item-edit-${it.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {data?.is_admin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-rose-600 hover:text-rose-700"
                                onClick={() => askConfirm(t("progress.deleteConfirm", { title: it.title }), () => delMut.mutate(it.id))}
                                data-testid={`work-item-delete-${it.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </>
  );

  return (
    <div className="space-y-6" data-testid="progress-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{t("progress.title")}</h1>
          <p className="text-sm text-slate-500">
            {mergedView ? t("progress.subtitleMerged") : t("progress.subtitleAll")}
          </p>
        </div>
      </div>

      {mergedView ? (
        <div className="space-y-8" data-testid="progress-merged-view">
          <div className="space-y-6">{workItemsSection}</div>
          <div className="space-y-4 border-t pt-6">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">{t("progress.yourActivityPlan")}</h2>
              <p className="text-sm text-slate-500">
                {t("progress.activityPlanSubtitle")}
              </p>
            </div>
            <ActivityPlans />
          </div>
        </div>
      ) : (
        <Tabs defaultValue="items">
          <TabsList data-testid="progress-tabs">
            <TabsTrigger value="items" data-testid="progress-tab-items">{t("progress.tabItems")}</TabsTrigger>
            <TabsTrigger value="plans" data-testid="progress-tab-plans">{t("progress.tabPlans")}</TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="mt-4 space-y-6">
            {workItemsSection}
          </TabsContent>

          <TabsContent value="plans" className="mt-4">
            <ActivityPlans />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-work-item-dialog">
          <DialogHeader><DialogTitle>{t("progress.addDialogTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {data?.is_admin && (
              <div className="space-y-1.5">
                <Label>{t("progress.party")}</Label>
                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                  <SelectTrigger data-testid="add-item-party-select"><SelectValue placeholder={t("progress.selectParty")} /></SelectTrigger>
                  <SelectContent>
                    {(data?.parties || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`add-item-party-${p.id}`}>{p.company_name} ({p.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("progress.titleLabel")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="add-item-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("progress.descOptional")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-item-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("progress.weightLabel")}</Label>
                <Input type="number" min="0.1" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="font-mono" data-testid="add-item-weight-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("progress.plannedDate")}</Label>
                <Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} data-testid="add-item-planned-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("progress.startDateL")}</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="add-item-start-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => addMut.mutate()} disabled={!form.title.trim() || addMut.isPending} data-testid="add-item-submit-button">
              {addMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent data-testid="edit-progress-dialog">
          <DialogHeader><DialogTitle>{t("progress.updateDialogPrefix")} — {editItem?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("progress.thProgress")}: <span className="font-mono font-bold" data-testid="edit-progress-value">{editForm.progress}%</span></Label>
              <Slider value={[editForm.progress]} min={0} max={100} step={1} onValueChange={([v]) => setEditForm({ ...editForm, progress: v })} data-testid="edit-progress-slider" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("progress.noteLabel")}</Label>
              <Input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} data-testid="edit-progress-note-input" placeholder={t("progress.notePlaceholder")} />
            </div>
            {data?.is_admin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("progress.weight")}</Label>
                  <Input type="number" min="0.1" step="0.1" value={editForm.weight} onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })} className="font-mono" data-testid="edit-item-weight-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("progress.plannedDate")}</Label>
                  <Input type="date" value={editForm.planned_date} onChange={(e) => setEditForm({ ...editForm, planned_date: e.target.value })} data-testid="edit-item-planned-date-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("progress.startDateL")}</Label>
                  <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} data-testid="edit-item-start-date-input" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => editMut.mutate()} disabled={editMut.isPending} data-testid="edit-progress-submit-button">
              {editMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
